import { useCallback, useEffect, useState } from "react";
import { Download, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { SelectField } from "../../components/site/FormBits";
import { api, apiError } from "../../lib/api";
import { labelOf } from "../../data/options";

export const STATUSES = [
  { value: "nouveau", label: "Nouveau" }, { value: "a_contacter", label: "À contacter" }, { value: "contacte", label: "Contacté" },
  { value: "rdv_prevu", label: "Rendez-vous prévu" }, { value: "ferme", label: "Fermé" },
];
const statusLabel = (v) => STATUSES.find((s) => s.value === v)?.label || v;
const TYPE_LABELS = { questionnaire: "Questionnaire", consultation: "Consultation", calculatrice: "Calculatrice", profil_risque: "Profil d'investisseur" };
const CALC_ROWS = { total_income: "Revenu total (calc.)", taxable_income: "Revenu imposable", total_tax: "Impôt total estimé", after_tax_income: "Revenu après impôt", average_rate: "Taux moyen", marginal_rate: "Taux marginal" };
const fmtDate = (iso) => new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });

const Row = ({ k, v }) => (
  <div className="grid grid-cols-[180px_1fr] gap-3 border-b border-[#EDE3D9] py-2 text-sm"><span className="text-[#242020]/60">{k}</span><span>{v || "—"}</span></div>
);

const LeadDialog = ({ lead, forfaitName, onClose, onSaved, onDeleted }) => {
  const [status, setStatus] = useState(lead.status);
  const [notes, setNotes] = useState(lead.notes || "");
  const a = lead.answers || {};
  const save = async () => {
    try {
      const { data } = await api.patch(`/admin/leads/${lead.id}`, { status, notes });
      toast.success("Fiche mise à jour");
      onSaved(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => {
    if (!window.confirm("Supprimer définitivement cette fiche prospect?")) return;
    await api.delete(`/admin/leads/${lead.id}`);
    toast.success("Fiche supprimée");
    onDeleted(lead.id);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-[#FFFDFB]" data-testid="lead-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{lead.full_name}</DialogTitle>
          <DialogDescription>{TYPE_LABELS[lead.type]} · {fmtDate(lead.created_at)}</DialogDescription></DialogHeader>
        <div>
          <Row k="Courriel" v={lead.email} /><Row k="Téléphone" v={lead.phone} /><Row k="Entreprise" v={lead.company} />
          <Row k="Secteur" v={lead.secteur_label} /><Row k="Métier" v={lead.metier_label} />
          <Row k="Statut professionnel" v={labelOf("statut", a.statut)} /><Row k="Années en affaires" v={labelOf("anciennete", a.anciennete)} />
          <Row k="Bénéfice / revenu" v={labelOf("benefice", a.benefice)} /><Row k="5 employés ou plus" v={labelOf("employes", a.employes)} />
          <Row k="Objectif" v={`${labelOf("objectif", a.objectif) || ""}${a.objectif_autre ? ` — ${a.objectif_autre}` : ""}`} />
          {lead.message && <Row k="Message" v={lead.message} />}
          {lead.risk_profile && <Row k="Profil d'investisseur" v={`${lead.risk_profile.profile} (${lead.risk_profile.score}/50)${lead.risk_profile.horizon_capped ? " — horizon < 2 ans" : ""}`} />}
          {lead.calculator && Object.entries(CALC_ROWS).map(([k, l]) => <Row key={k} k={l} v={k.endsWith("rate") ? `${lead.calculator.results[k]} %` : `${(lead.calculator.results[k] ?? lead.calculator.inputs[k] ?? 0).toLocaleString("fr-CA")} $`} />)}
          <Row k="Forfait suggéré / joint" v={lead.recommendation?.forfait_slug ? forfaitName(lead.recommendation.forfait_slug) : "Aucun (consultation)"} />
          <Row k="Règle déclenchée" v={lead.recommendation?.rule_name} />
          <Row k="Rendez-vous" v={lead.booking ? `${fmtDate(lead.booking.start)} (Teams)` : lead.callback_requested_at ? `Rappel demandé le ${fmtDate(lead.callback_requested_at)}` : ""} />
          <Row k="Consentements" v={`Données : oui · Marketing : ${lead.consents?.marketing ? "oui" : "non"} (${fmtDate(lead.consents?.at)})`} />
          <Row k="Synchronisation GHL" v="Non configurée" />
          {lead.duplicate_of && <Row k="Doublon possible" v="Une fiche existe déjà avec ce courriel" />}
        </div>
        <div className="grid gap-4 pt-2 sm:grid-cols-[220px_1fr]">
          <SelectField value={status} onChange={setStatus} options={STATUSES} testid="lead-status-select" />
          <textarea rows={2} className="field-input" placeholder="Notes de suivi" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="lead-notes-input" />
        </div>
        <div className="flex justify-between pt-2">
          <button onClick={del} className="inline-flex items-center gap-2 text-sm text-[#b42318]" data-testid="lead-delete-button"><Trash2 className="h-4 w-4" />Supprimer</button>
          <button onClick={save} className="btn-primary !py-2.5" data-testid="lead-save-button">Enregistrer</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function LeadsTab({ forfaits, forfaitName, onChange }) {
  const [filters, setFilters] = useState({ q: "", status: "", forfait: "", type: "" });
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/leads", { params: filters }).then((r) => setLeads(r.data)).catch((e) => toast.error(apiError(e)));
  }, [filters]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  const setF = (k) => (v) => setFilters((p) => ({ ...p, [k]: v === "tous" ? "" : v }));

  const exportCsv = async () => {
    const r = await api.get("/admin/leads/export.csv", { params: filters, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "prospects-struktiv.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const refresh = () => { load(); onChange(); };

  return (
    <div className="space-y-5" data-testid="leads-tab">
      <div className="grid gap-3 md:grid-cols-[1.5fr_1fr_1fr_1fr_auto]">
        <div className="relative"><Search className="absolute left-3 top-3.5 h-4 w-4 text-[#242020]/40" />
          <input className="field-input !pl-9" placeholder="Rechercher nom, courriel, métier…" value={filters.q} onChange={(e) => setF("q")(e.target.value)} data-testid="leads-search-input" /></div>
        <SelectField value={filters.status || "tous"} onChange={setF("status")} options={[{ value: "tous", label: "Tous les statuts" }, ...STATUSES]} testid="leads-filter-status" />
        <SelectField value={filters.forfait || "tous"} onChange={setF("forfait")} options={[{ value: "tous", label: "Tous les forfaits" }, ...forfaits.map((f) => ({ value: f.slug, label: f.name })), { value: "aucun", label: "Aucun forfait" }]} testid="leads-filter-forfait" />
        <SelectField value={filters.type || "tous"} onChange={setF("type")} options={[{ value: "tous", label: "Tous les types" }, { value: "questionnaire", label: "Questionnaire" }, { value: "consultation", label: "Consultation" }, { value: "calculatrice", label: "Calculatrice" }, { value: "profil_risque", label: "Profil d'investisseur" }]} testid="leads-filter-type" />
        <button onClick={exportCsv} className="btn-outline" data-testid="leads-export-csv"><Download className="h-4 w-4" />CSV</button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-[#EDE3D9] bg-[#FFFDFB]">
        <table className="w-full min-w-[820px] text-sm" data-testid="leads-table">
          <thead className="bg-[#EDE3D9]/40 text-left text-xs uppercase tracking-wider text-[#242020]/60">
            <tr><th className="p-3">Date</th><th className="p-3">Nom</th><th className="p-3">Type</th><th className="p-3">Métier</th><th className="p-3">Forfait</th><th className="p-3">Statut</th></tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} onClick={() => setOpen(l)} className="cursor-pointer border-t border-[#EDE3D9] hover:bg-[#FAF6F1]" data-testid={`lead-row-${l.id}`}>
                <td className="p-3 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                <td className="p-3"><p className="font-medium">{l.full_name}</p><p className="text-xs text-[#242020]/60">{l.email}</p></td>
                <td className="p-3">{TYPE_LABELS[l.type]}</td>
                <td className="p-3">{l.metier_label || "—"}</td>
                <td className="p-3">{l.recommendation?.forfait_slug ? forfaitName(l.recommendation.forfait_slug) : <span className="text-[#242020]/50">Aucun</span>}</td>
                <td className="p-3"><span className="rounded-full bg-[#650D23]/10 px-2.5 py-1 text-xs text-[#650D23]">{statusLabel(l.status)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!leads.length && <p className="p-8 text-center text-sm text-[#242020]/60" data-testid="leads-empty">Aucun prospect pour ces critères.</p>}
      </div>
      {open && <LeadDialog lead={open} forfaitName={forfaitName} onClose={() => setOpen(null)}
        onSaved={() => { setOpen(null); refresh(); }} onDeleted={() => { setOpen(null); refresh(); }} />}
    </div>
  );
}
